import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import { PathRoutes } from 'src/const/Routes/PathRoutes';
import {
  Oth360InviteContext,
  Oth360InviteMode,
  saveOth360InviteContext,
} from './oth360InviteStorage';

type Oth360AuthResponse = {
  result: {
    subjectName: string;
    subjectEmail: string;
    mode: Oth360InviteMode;
    hasSubmitted?: boolean;
    employeeLink?: string | null;
    evaluationRoundId?: string;
    roundId?: string;
    form: {
      id: string;
      version?: number;
      schemaJson: unknown;
    } | null;
  };
};

type ErrorState = { message: string } | null;

export const Oth360InvitePage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const token = useMemo(() => {
    const fromSearch = new URLSearchParams(location.search)
      .get('token')
      ?.trim();
    if (fromSearch) {
      return fromSearch;
    }

    // Defensive fallbacks in case the token ends up in window.search or hash (e.g. copy/paste variants).
    const winSearch = new URLSearchParams(window.location.search)
      .get('token')
      ?.trim();
    if (winSearch) {
      return winSearch;
    }

    const hash = window.location.hash || '';
    const qIndex = hash.indexOf('?');
    if (qIndex >= 0) {
      const hashQuery = hash.slice(qIndex + 1);
      const fromHash = new URLSearchParams(hashQuery).get('token')?.trim();
      if (fromHash) {
        return fromHash;
      }
    }

    return '';
  }, [location.search]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorState>(null);
  const [ctx, setCtx] = useState<Oth360InviteContext | null>(null);

  useLayoutEffect(() => {
    let cancelled = false;

    async function resolveInviteToken(inviteToken: string) {
      if (!inviteToken) {
        setError({ message: 'Missing token' });
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // IMPORTANT: Use same-origin for OTH 360 auth so we avoid CORS + cookie-on-wrong-domain issues on staging/prod.
        // This requires the hosting stack to proxy /api/* to the backend in non-local envs.
        const url = new URL('/api/oth/360/auth', window.location.origin);

        url.searchParams.set('token', inviteToken);

        const res = await fetch(url.toString(), {
          credentials: 'include',
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!res.ok) {
          let bodyText = '';
          try {
            bodyText = await res.text();
          } catch {
            // ignore
          }

          // Backend may return 400 or 404 depending on why auth fails.
          // Prefer inspecting the message so "invalid token" never shows as "missing token".
          const lower = bodyText.toLowerCase();
          const looksInvalid =
            lower.includes('invalid token') || lower.includes('expired');
          const looksMissing = lower.includes('token is required');

          if (res.status === 404 || looksInvalid) {
            throw new Error('Invalid/expired link');
          }
          if (res.status === 400 || looksMissing) {
            throw new Error('Missing token');
          }

          throw new Error('Something went wrong');
        }

        const contentType = res.headers.get('content-type') || '';
        if (!contentType.toLowerCase().includes('application/json')) {
          throw new Error(
            'Unexpected response from server. Please make sure the API is running and restart the web app dev server.'
          );
        }

        const data = (await res.json()) as Partial<Oth360AuthResponse>;

        const employeeLink =
          typeof data.result?.employeeLink === 'string'
            ? data.result.employeeLink
            : null;

        const evaluationRoundId =
          typeof data.result?.evaluationRoundId === 'string'
            ? data.result.evaluationRoundId
            : typeof data.result?.roundId === 'string'
              ? data.result.roundId
              : null;

        const mode = data.result?.mode;
        const subjectName = data.result?.subjectName;
        const hasSubmitted = data.result?.hasSubmitted === true;

        if (!mode || !subjectName) {
          throw new Error('Unexpected response from server.');
        }

        const rawForm = data.result?.form ?? null;
        const form =
          rawForm && rawForm.id
            ? {
                id: rawForm.id,
                ...(typeof rawForm.version === 'number'
                  ? { version: rawForm.version }
                  : {}),
                schemaJson: rawForm.schemaJson,
              }
            : null;

        const nextCtx: Oth360InviteContext = {
          mode,
          subjectName,
          hasSubmitted,
          form,
          employeeLink,
          evaluationRoundId,
        };

        if (cancelled) {
          return;
        }

        saveOth360InviteContext(nextCtx);

        const to =
          nextCtx.mode === 'EMPLOYEE'
            ? PathRoutes.Oth360FormRoute
            : nextCtx.hasSubmitted
              ? PathRoutes.Oth360ResultsRoute
              : PathRoutes.Oth360FormRoute;

        navigate(to, { replace: true });

        setCtx(nextCtx);
      } catch (e) {
        if (!cancelled) {
          const message =
            e instanceof Error && e.message
              ? e.message
              : 'Something went wrong';
          setError({ message });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    resolveInviteToken(token);
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'grid',
          placeItems: 'center',
          px: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 520,
            p: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ErrorOutlineRoundedIcon color="error" />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Something went wrong
              </Typography>
            </Stack>

            <Typography color="text.secondary">
              {error.message || 'Please try opening your invite link again.'}
            </Typography>

            <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
              <Button
                variant="contained"
                onClick={() => window.location.reload()}
              >
                Try again
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (isLoading || !ctx) {
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'grid',
          placeItems: 'center',
          px: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 520,
            p: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              360 Evaluation
            </Typography>
            <Typography color="text.secondary">
              Preparing your session…
            </Typography>
            <Box sx={{ pt: 1 }}>
              <MaterialCircularProgress />
            </Box>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return <></>;
};
